import { apiClient } from './client';
import type { MetricStatus, MetricDirection } from '@/lib/scorecards/status';

/**
 * Client for `/api/insights/*` (ADR-033) — the generic, configurable
 * KPI/Insights dashboard. Stays in lockstep with
 * `server/src/routes/insights.routes.ts`; the interfaces below mirror
 * the server's response shapes exactly.
 */

export type MetricUnit = 'count' | 'dollars' | 'percent' | 'ratio' | 'custom';
export type MetricDataSourceKind = 'manual' | 'integration' | 'formula';
export type InsightsGroupBy = 'none' | 'department' | 'seat';

export interface InsightsSummary {
  totalMetrics: number;
  withData: number;
  onTrack: number;
  atRisk: number;
  offTrack: number;
  noData: number;
  /** round(onTrack / withData * 100); null when withData === 0. */
  onTrackPct: number | null;
}

export interface InsightsMetric {
  id: string;
  title: string;
  unit: MetricUnit;
  unitLabel: string | null;
  goal: number;
  direction: MetricDirection;
  dataSourceKind: MetricDataSourceKind;
  /** Seat holder name preferred; else owner employee; vacant seat → null. */
  owner: { kind: 'seat' | 'employee'; name: string } | null;
  departmentId: string | null;
  departmentName: string | null;
  seatId: string | null;
  seatName: string | null;
  /** Latest entry IN window, or null when the metric has no data in window. */
  current: { weekOf: string; value: number; status: MetricStatus } | null;
  /** The entry one step before `current` (for week-over-week). */
  previous: { weekOf: string; value: number } | null;
  /** round((current - previous)/|previous|*100); null if no previous or previous === 0. */
  wowDeltaPct: number | null;
  /** round(current / goal * 100); null if no current or goal === 0. */
  attainmentPct: number | null;
  /** length === weekWindow, aligned to `weeks` (DESCENDING); null value = no entry that week. */
  series: { weekOf: string; value: number | null; status: MetricStatus | null }[];
}

export interface InsightsGroup {
  /** departmentId or seatId (or '__none__' for the unassigned bucket). */
  key: string;
  label: string;
  totalMetrics: number;
  withData: number;
  onTrack: number;
  atRisk: number;
  offTrack: number;
  noData: number;
  onTrackPct: number | null;
}

export interface InsightsOverview {
  /** Echoed, clamped week window (4..26). */
  weekWindow: number;
  groupBy: InsightsGroupBy;
  /** ISO dates (YYYY-MM-DD), DESCENDING (index 0 = most recent Monday). */
  weeks: string[];
  summary: InsightsSummary;
  /** Ordered by Metric.order asc, title asc. */
  metrics: InsightsMetric[];
  /** null when groupBy === 'none'. */
  groups: InsightsGroup[] | null;
  featuredMetricIds: string[];
}

export interface InsightsConfig {
  featuredMetricIds: string[];
  defaultGroupBy: InsightsGroupBy;
  weekWindow: number;
}

export interface GetInsightsOverviewQuery {
  weeks?: number;
  groupBy?: InsightsGroupBy;
}

export async function getInsightsOverview(q: GetInsightsOverviewQuery = {}): Promise<InsightsOverview> {
  const params = new URLSearchParams();
  if (q.weeks !== undefined) params.set('weeks', String(q.weeks));
  if (q.groupBy !== undefined) params.set('groupBy', q.groupBy);
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const res = await apiClient.get<{ data: InsightsOverview }>(`/api/insights/overview${suffix}`);
  return res.data.data;
}

export async function getInsightsConfig(): Promise<InsightsConfig> {
  const res = await apiClient.get<{ data: InsightsConfig }>('/api/insights/config');
  return res.data.data;
}

export interface UpdateInsightsConfigRequest {
  featuredMetricIds?: string[];
  defaultGroupBy?: InsightsGroupBy;
  weekWindow?: number;
}

export async function updateInsightsConfig(body: UpdateInsightsConfigRequest): Promise<InsightsConfig> {
  const res = await apiClient.put<{ data: InsightsConfig }>('/api/insights/config', body);
  return res.data.data;
}
